import { User, Building2, Briefcase } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';

// The questions both "create template" dialogs start with: name, description, which kind of
// Bullhorn record it is for, and where the finished document is filed in Bullhorn.
// Class names are written out in full so Tailwind can see them.
const KINDS = [
  {
    key: 'candidate', label: 'Candidate', hint: 'For job applicants', Icon: User,
    on: 'border-[#4CAF50] bg-[#4CAF50]/10', off: 'border-border hover:border-[#4CAF50]/50',
    tileOn: 'bg-[#4CAF50]', tileOff: 'bg-[#4CAF50]/20', iconOff: 'text-[#4CAF50]',
  },
  {
    key: 'contact', label: 'Contact', hint: 'For client contacts', Icon: Building2,
    on: 'border-[#F9A825] bg-[#F9A825]/10', off: 'border-border hover:border-[#F9A825]/50',
    tileOn: 'bg-[#F9A825]', tileOff: 'bg-[#F9A825]/20', iconOff: 'text-[#F9A825]',
  },
  {
    key: 'placement', label: 'Placement', hint: 'For active placements', Icon: Briefcase,
    on: 'border-[#1a4a5e] bg-[#1a4a5e]/10', off: 'border-border hover:border-[#1a4a5e]/50',
    tileOn: 'bg-[#1a4a5e]', tileOff: 'bg-[#1a4a5e]/20', iconOff: 'text-[#1a4a5e]',
  },
];

export default function TemplateBasicsFields({ value, onChange, fileTypes }) {
  const set = (patch) => onChange({ ...value, ...patch });

  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="name">Template Name</Label>
        <Input
          id="name"
          value={value.name}
          onChange={(e) => set({ name: e.target.value })}
          placeholder="e.g., Employment Contract"
          className="input-focus"
          data-testid="template-name-input"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="description">Description (optional)</Label>
        <p className="text-xs text-muted-foreground">
          Shown at the top of the document when the {value.entity_type === 'contact' ? 'contact' : value.entity_type === 'placement' ? 'recipient' : 'candidate'} opens it to complete — you can change this later from the template's Settings.
        </p>
        <Textarea
          id="description"
          value={value.description}
          onChange={(e) => set({ description: e.target.value })}
          placeholder="Brief description of this template"
          className="input-focus"
          rows={3}
          data-testid="template-description-input"
        />
      </div>

      <div className="space-y-2">
        <Label>Template Type</Label>
        <div className="grid grid-cols-3 gap-3">
          {KINDS.map(({ key, label, hint, Icon, on, off, tileOn, tileOff, iconOff }) => {
            const selected = value.entity_type === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => set({ entity_type: key })}
                className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${selected ? on : off}`}
                data-testid={`entity-type-${key}`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${selected ? tileOn : tileOff}`}>
                  <Icon className={`w-5 h-5 ${selected ? 'text-white' : iconOff}`} />
                </div>
                <div className="text-left">
                  <p className="font-medium">{label}</p>
                  <p className="text-xs text-muted-foreground">{hint}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-2">
        <Label>Bullhorn File Type</Label>
        <select
          value={value.bullhorn_file_type}
          onChange={(e) => set({ bullhorn_file_type: e.target.value })}
          className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
          data-testid="bullhorn-file-type-select"
        >
          <option value="">Select file type...</option>
          {fileTypes.map((type) => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
        <p className="text-xs text-muted-foreground">
          This determines where the document is saved in Bullhorn
        </p>
      </div>
    </>
  );
}
